import { useMemo } from 'react';
import moment from 'moment';
import { useFormValues } from '@s20/components/forms/FormContext';
import { getWeekStartMoment } from '@s20/common/util/date';
import { useWeekStartsOn } from '@s20/hooks/tenant.hooks';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../../Common';
import { doesJobPassPlanningFilter, doesJobPassSearchText } from '../../../dispatch/Dispatch';

export const useJobQueryKey = () => {
    const {
        selectedDate: date, jobSearchText, selectedPlanningFilter, dispatchGroupId,
        replacingAssignmentId, replacementOriginalResourceId,
    } = useFormValues();

    const weekStartsOn = useWeekStartsOn();
    const selectedDate = moment(date);
    // First day of the tenant week: the same anchor the job forms store as Job.WeekOfDate
    const startDate = getWeekStartMoment(selectedDate, weekStartsOn).format();

    const queryKey = useMemo(() => ['dispatchJobs', startDate, dispatchGroupId], [startDate, dispatchGroupId]);
    const queryFn = () => {
        const weekStart = moment(startDate);
        const weekEnd = weekStart.clone().add(6, 'days');

        const payload = {
            // Send date-only (no local TZ offset). The backend filters on WeekOfDate.Date via
            // Newtonsoft (RoundtripKind); a local-offset datetime gets converted to server-local
            // time, injecting a time-of-day (or rolling the calendar day), so the exact-date
            // match (WeekOfDate.Date == WeekStartDate) never matches and every week returns empty.
            weekStartDate: weekStart.format('YYYY-MM-DD'),
            weekEndDate: weekEnd.format('YYYY-MM-DD'),
            dispatchGroupId,
        };

        return httpClient.post(
            ApiRoutes.dispatch.jobs(),
            payload,
            httpClient.format.json,
        );
    };

    const select = (response) => {
        const day = selectedDate.day();

        const result = (response?.rows || []).filter((job) => {
            const assignment = job.assignments[day];

            return !!assignment?.jobId && doesJobPassSearchText(job, jobSearchText) && doesJobPassPlanningFilter(job, day, selectedPlanningFilter);
        });

        // While an assignment is mid-replacement, present its slot as open (empty) to every
        // consumer of this query — the job cards, the available-resource count, and the
        // resource picker — so the replacement can be chosen. Done at read-time (not in the
        // cache) so a background refetch can't revert it before the replacement is persisted.
        if (!replacingAssignmentId) {
            return result;
        }

        // Collapse the slot only while it still shows the original resource being replaced.
        // Once the replace is assigned, the slot shows the new resource (id no longer matches),
        // so it stops collapsing and the new resource appears without flashing empty for a frame.
        const isMidReplacement = (resource) =>
            resource.jobAssignmentId === replacingAssignmentId
            && (!resource.assignment || resource.assignment.id === replacementOriginalResourceId);

        // The work truck operated by the staff being replaced should disappear with them,
        // mirroring "Remove this staff", and free its slot for the replacement's own truck.
        // The backend teardown removes it (and anything on it) on assign; this hides it now.
        const isLinkedToReplacedStaff = (resource) =>
            !!replacementOriginalResourceId && resource.assignedEmployee?.id === replacementOriginalResourceId;

        const collapse = (resource) => ({
            ...resource, jobAssignmentId: null, assignment: null, assignedEmployee: null, assignedEquipment: null,
        });

        const clearStaff = (resources) => resources?.map((r) => (isMidReplacement(r) ? collapse(r) : r));
        const clearEquipment = (resources) => resources?.map((r) =>
            (isMidReplacement(r) || isLinkedToReplacedStaff(r) ? collapse(r) : r));

        return result.map((job) => {
            const dayAssignment = job.assignments?.[day];

            if (!dayAssignment) {
                return job;
            }

            return {
                ...job,
                assignments: {
                    ...job.assignments,
                    [day]: {
                        ...dayAssignment,
                        staff: clearStaff(dayAssignment.staff),
                        equipment: clearEquipment(dayAssignment.equipment),
                    },
                },
            };
        });
    };

    return {
        queryKey,
        queryFn,
        select,
        startDate,
        dispatchGroupId,
        date,
    };
};